import { fetchSessionFromApi } from './api';
import { decodeToken, resolveToken } from './jwt';
import type { AuthConfig, IbxPayload, StarkSessionResponse, UserSessionInfo } from './types';

// Guarded so this module doesn't throw when bundled into a runtime with no
// `process` global (e.g. wakanda-ui's Angular build) — those runtimes call
// configureAuth() explicitly with their own env mechanism anyway.
let _starkUrl = typeof process !== 'undefined' ? (process.env.NEXT_PUBLIC_API_URL ?? '') : '';

export function configureAuth(config: AuthConfig): void {
  _starkUrl = config.starkUrl;
}

export async function getUserSessionInfo(): Promise<UserSessionInfo | null> {
  const token = resolveToken();

  if (!token) {
    throw new Error('Unauthorized');
  }

  try {
    const payload: IbxPayload = await decodeToken(token);

    const userId: number | null = payload.userId ?? null;
    const profileId: number | null = payload.profileId ?? null;

    if (userId !== null || profileId !== null) {
      // fetchSessionFromApi's declared return type (UserSessionInfo) doesn't
      // match stark's actual response envelope ({ user: UserSessionInfo | null
      // }). get<T>() only performs a compile-time cast, no runtime check, so
      // this reinterpretation is safe. Handled here rather than in api.ts —
      // see StarkSessionResponse in types.ts.
      const response = (await fetchSessionFromApi(_starkUrl, token)) as unknown as StarkSessionResponse | null;
      return response?.user ?? null;
    }

    const guest = {
      userId: null,
      profileId: null,
      isLoggedIn: false,
      cart: {
        id: payload.cartId ?? null,
        count: payload.cartCount ?? 0,
      },
    };
    return guest;
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('HTTP 401')) {
      throw new Error('Unauthorized');
    }
    return null;
  }
}
